import React, {useCallback, useState} from 'react';
import {UserBaseDTO} from 'hemwb-api';
import {useDispatch} from 'react-redux';
import {useCurrentUser} from '../../../../../store/auth';
import {Form, useForm} from '../../../../core/Form';
import {fullNameWestern} from '../../../../../store/user/utils';
import {FormControl, FormGroup, Validators} from 'react-reactive-form';
import {Button, Input, message as antdMessage, Tag, Typography} from 'antd';
import {ShareAltOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import {getUrlRoute, MRAppRoutes} from '../../../../router/routes';
import {modelShareEmail} from '../../../../../store/model';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {ModelActionProps} from '../../../actionMenuTypes';
import {MODEL_ACTION_MENU_IDS} from '../../../../../test/types';
import UserSelect from '../../../../core/AntdSelect/UserSelect';

const {Text, Title} = Typography;
const headingTitleStyle = {margin: '8px 0 1rem 0', fontSize: '2.2rem', lineHeight: '1.6rem', fontWeight: 700 as const};

const userFindOptions = {limit: 10, includeNpa: false, includeUnenrolled: true};
const defaultMessage = '{user} has invited you to access and explore information for: {name}, using the link below:';

const ModelShareSideBarModal: React.FC<ModelActionProps> = ({onCancel, onSuccess, model}) => {
  const dispatch = useDispatch();
  const url = `${window.location.origin}/#${getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: model.uuid})}`;
  const currentUser = useCurrentUser();
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const form = useForm(
    new FormGroup({
      users: new FormControl([], Validators.required),
      message: new FormControl('', [Validators.required]),
    }),
    {
      users: [],
      message: defaultMessage.replace('{user}', fullNameWestern(currentUser)).replace('{name}', model.name),
    },
  );

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      antdMessage.success('Link copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (e) {
      antdMessage.error('Failed to copy link');
    }
  }, [url]);

  const handleUsersChange = useCallback(
    (_values: string[], options: any) => {
      const users = (Array.isArray(options) ? options : []).map((opt: any) => opt.user as UserBaseDTO).filter(Boolean);
      form.controls.users.setValue(users);
      form.controls.users.markAsTouched();
    },
    [form],
  );

  const handleDeleteUser = useCallback(
    (user: UserBaseDTO) => {
      form.controls.users.setValue(form.controls.users.value.filter((u: UserBaseDTO) => u.id !== user.id));
      form.controls.users.markAsTouched();
    },
    [form],
  );

  const handleSubmit = useCallback(() => {
    return modelShareEmail(
      dispatch,
      model.uuid,
      form.controls.users.value.map((u: UserBaseDTO) => u.id),
      form.controls.message.value,
    ).then(() => {
      dispatch(messageAdd('Email has been sent', MessageTypes.SUCCESS));
      onSuccess?.();
    });
  }, [dispatch, model, form, onSuccess]);

  return (
    <Form
      FieldGroupProps={{strict: false}}
      group={form}
      onSubmit={handleSubmit}
      className=""
      {...tid('model-share-form')}
      render={({valid, submitting}) => {
        const selectedUsers: UserBaseDTO[] = form.controls.users.value || [];

        return (
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            {/* Title */}
            <Title level={5} style={{...headingTitleStyle, marginBottom: 0}}>
              Share Model
            </Title>

            {/* Model name */}
            <Text style={{fontSize: '14px', color: '#262626', lineHeight: '1.5'}}>{model.name}</Text>

            {/* Users field */}
            <div>
              <label style={{display: 'block', marginBottom: '4px', fontSize: '14px', color: 'rgba(0,0,0,0.85)'}}>
                Send an email to selected users <span style={{color: '#ff4d4f'}}>*</span>
              </label>
              <UserSelect
                mode="multiple"
                placeholder="Search for users..."
                value={selectedUsers.map((u: UserBaseDTO) => u.id)}
                onChange={handleUsersChange}
                userFindOptions={userFindOptions}
                hideSelectedValue
                style={{width: '100%'}}
                {...tid('input', 'users')}
              />
              {/* Selected user tags */}
              {selectedUsers.length > 0 && (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px'}}>
                  {selectedUsers.map((user: UserBaseDTO) => (
                    <Tag
                      key={user.id}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        handleDeleteUser(user);
                      }}
                      color="#00857c"
                      style={{display: 'inline-flex', alignItems: 'center', padding: '2px 8px', margin: 0}}
                    >
                      {fullNameWestern(user)}
                    </Tag>
                  ))}
                </div>
              )}
            </div>

            {/* Message field */}
            <div>
              <label style={{display: 'block', marginBottom: '4px', fontSize: '14px', color: 'rgba(0,0,0,0.85)'}}>
                Customize your email message <span style={{color: '#ff4d4f'}}>*</span>
              </label>
              <Input.TextArea
                value={form.controls.message.value}
                onChange={(e) => {
                  form.controls.message.setValue(e.target.value);
                  form.controls.message.markAsTouched();
                }}
                rows={4}
                style={{resize: 'vertical'}}
                {...tid('input', 'message')}
              />
            </div>

            {/* Direct link */}
            <div>
              <label style={{display: 'block', marginBottom: '4px', fontSize: '14px', color: 'rgba(0,0,0,0.85)'}}>
                Direct Link
              </label>
              <div style={{display: 'flex', gap: '8px'}}>
                <Input value={url} readOnly style={{flex: 1, backgroundColor: '#f5f5f5', minWidth: 0}} />
                <Button onClick={handleCopyLink} disabled={isCopied} {...tid('copy-link-button')}>
                  {isCopied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
            </div>

            {/* Footer buttons */}
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px'}}>
              <Button onClick={onCancel} disabled={submitting} {...tid(MODEL_ACTION_MENU_IDS.SHARE, 'cancel')}>
                Cancel
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                loading={submitting}
                disabled={!valid}
                icon={<ShareAltOutlined />}
                {...tid(MODEL_ACTION_MENU_IDS.SHARE, 'submit')}
              >
                Share
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
};

export default ModelShareSideBarModal;
